import { Component, ComponentFactoryResolver, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
	BroadcastService,
	GridBaseComponent,
	SubContentService,
	ToasterType,
	SetIsHasPermissions,
} from '@cv-invision/core';
import { CvDateFormatter } from '@cv-invision/cv.component-library.ux';
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NexsysMonitorService } from '../../service/nexsys-monitor.service';
import { ORIGIN, URLS } from '../model/nexsys-monitor.constant';
import { NexsysOrderLogsColumnsModel } from '../model/nexsys-order-logs-columns.model';
import { SetNexsysOrderLogInfo } from '../state/actions/nexsys-monitor.actions';
import { CustomActionRetryCellComponent } from './custom-action-grid-cell/custom-action-grid-cell.component';
import { LogsOrderDescriptionComponent } from './logs-description/logs-description.component';
import { SetRetryActions } from '../../state/actions/global-configuration-enums.actions';

@Component({
	selector: 'cv-nexsys-logs-order',
	templateUrl: './nexsys-logs-order.component.html',
	styleUrls: ['./nexsys-logs-order.component.scss']
})
export class NexsysLogsOrderComponent extends GridBaseComponent implements OnInit, OnDestroy {
	private atComponentDestroy: Subject<any> = new Subject<any>();
	orderId: string;
	externalId: string;
	nexsysOrderBaseData: any;
	rowClassRules: any;
	origin: string;

	constructor(
		private nexsysMonitorService: NexsysMonitorService,
		private activateRoute: ActivatedRoute,
		private componentFactoryResolver: ComponentFactoryResolver,
		private subContentService: SubContentService,
		private broadcastService: BroadcastService,
		private store: Store,
		private router: Router,
		private dateService: CvDateFormatter
	) {
		super();

		this.rowClassRules = {
			'error-row': function(params: any): boolean {
				return !params.data.valid;
			}
		};
	}

	ngOnInit(): void {
		const url = this.router.url;

		Object.keys(URLS).forEach(urlKey => {
			if (url.includes(URLS[urlKey])) {
				const key = Object.keys(ORIGIN).find(originKey => {
					const pattern = /[^a-zA-Z0-9]/gm;
					const cleanUrlKey = urlKey.toLowerCase().replace(pattern, '');
					const cleanOriginKey = (ORIGIN[originKey as keyof typeof ORIGIN] || '')
						.toLowerCase()
						.replace(pattern, '');

					return cleanOriginKey === cleanUrlKey;
				});

				if (key) {
					this.origin = ORIGIN[key];
					this.orderId = this.activateRoute.snapshot.paramMap.get('id');
					this.externalId = null;

					switch(ORIGIN[key]) {
						case ORIGIN.Xpanse:
							this.orderId = this.activateRoute.snapshot.paramMap.get('transactionId');
							this.externalId = this.activateRoute.snapshot.paramMap.get('SPNId');
							break;
						case ORIGIN.ValueLink:
							this.externalId = this.activateRoute.snapshot.paramMap.get('providerClientId');
							break;
					}
				}
			}
		})

		this.store.dispatch(new SetRetryActions(this.origin)).toPromise();

		this.gridOptions.columnDefs = new NexsysOrderLogsColumnsModel(this.dateService).columns;
		this.gridOptions.frameworkComponents = {
			customActionRetryCellComponent: CustomActionRetryCellComponent
		};
		this.loadGridData();
		// subscribe to the reload list event.
		this.nexsysMonitorService
			.getReloadListListener()
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((reload: boolean) => {
				if (reload) {
					this.loadGridData();
				}
			});
	}
	/**
	 * Selected order
	 */
	public onSelectionChanged(): void {
		const selectedRows = this.gridApi.getSelectedRows();
		const selectedRow = selectedRows[0];
		if (selectedRow) {
			this.openSubComponent(selectedRow);
		} else {
			this.subContentService.close();
		}
	}
	/**
	 * Open the component with the Selected Row
	 * @param selectedRow
	 */
	private openSubComponent(selectedRow: any = null): void {
		const source = this.origin;
		this.subContentService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: LogsOrderDescriptionComponent,
				data: { trans: selectedRow, ...this.nexsysOrderBaseData, source }
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe();
	}

	/**
	 * open nexsys log order
	 */
	loadGridData(): void {
		this.nexsysMonitorService
			.getNexsysOrderTransLog(this.orderId, this.origin, this.externalId)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(
				result => {
					this.nexsysOrderBaseData = Object.assign({}, result);
					delete this.nexsysOrderBaseData['transactionLogs'];
					this.store.dispatch(new SetNexsysOrderLogInfo(this.nexsysOrderBaseData));
					this.gridData = result.transactionLogs;
				},
				error => {
					this.errorMsgFunction(error.error.error);
				}
			);
	}

	/**
	 * Display error message weather the operation wast´n success
	 * @param error
	 */
	errorMsgFunction(error: string): void {
		const event = {
			error: error,
			autoClose: true
		};
		this.broadcastService.broadcast({
			name: ToasterType.ERROR,
			event: event
		});

		this.store.dispatch(new SetIsHasPermissions(null));
	}

	/**
	 * On component destroy
	 */
	ngOnDestroy(): void {
		this.subContentService.close();
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
